import React from 'react';

import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  FILTER_DEFINITIONS,
  type FilterKey,
  PRIMARY_FILTERS,
  type ProductFilters,
  defaultFilters,
  latestDay,
  quickRanges,
} from './model';

type Props = {
  filters: ProductFilters;
  options: Record<string, Array<string>>;
  onChange: (change: Partial<ProductFilters>) => void;
  advanced: boolean;
  onAdvancedChange: () => void;
  /** Period Comparisons reports a fixed window, so it shows that window instead of date inputs. */
  fixedWindow?: string;
};

/** Option lists stop at 500 values, so longer lists also accept a typed value. */
const OPTION_LIMIT = 500;

export default function ProductPerformanceFilters({
  filters,
  options,
  onChange,
  advanced,
  onAdvancedChange,
  fixedWindow,
}: Props) {
  const saved = defaultFilters(filters.reportView);
  const maxDay = latestDay(filters.reportView);
  const select = (key: FilterKey, values: Array<string>) =>
    onChange({ selections: { ...filters.selections, [key]: values } });
  const isSaved = (key: FilterKey) =>
    JSON.stringify([...filters.selections[key]].sort()) === JSON.stringify([...saved.selections[key]].sort());
  const active = FILTER_DEFINITIONS.filter(({ key }) => !isSaved(key));
  const field = ({ key, label }: (typeof FILTER_DEFINITIONS)[number]) => {
    const selected = filters.selections[key];
    const listed = options[key] ?? [];
    const choices = [...new Set([...listed, ...selected])];
    return (
      <Autocomplete
        key={key}
        multiple
        freeSolo={listed.length >= OPTION_LIMIT}
        disableCloseOnSelect
        size="small"
        limitTags={1}
        options={choices}
        value={selected}
        onChange={(_, values) => select(key, values.map(String))}
        renderTags={(values, getTagProps) =>
          values.map((option, index) => {
            const { key: tagKey, ...tagProps } = getTagProps({ index });
            return <Chip key={tagKey} {...tagProps} label={option} size="small" />;
          })
        }
        renderOption={(props, option, { selected: checked }) => (
          <Box component="li" {...props} key={option}>
            <Checkbox checked={checked} size="small" sx={{ mr: 0.5 }} />
            {option}
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={selected.length ? undefined : 'All values'}
            helperText={
              listed.length >= OPTION_LIMIT ? `First ${OPTION_LIMIT} values listed; type to add another` : undefined
            }
          />
        )}
        sx={{ minWidth: 0, '& .MuiAutocomplete-tag': { maxWidth: 'calc(100% - 45px)' } }}
      />
    );
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, minWidth: 0 }}>
      <Stack gap={1.5}>
        <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
          {fixedWindow ? (
            <Typography variant="body2" color="text.secondary">
              {fixedWindow}
            </Typography>
          ) : (
            <>
              {(['from', 'to'] as const).map((key) => (
                <TextField
                  key={key}
                  label={key === 'from' ? 'From' : 'To'}
                  type="date"
                  size="small"
                  value={filters[key]}
                  onChange={(event) => onChange({ [key]: event.target.value })}
                  slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: maxDay } }}
                  sx={{ flex: '1 1 150px', maxWidth: { sm: 180 } }}
                />
              ))}
              {quickRanges(filters.reportView).map((range) => (
                <Button key={range.label} size="small" onClick={() => onChange({ from: range.from, to: range.to })}>
                  {range.label}
                </Button>
              ))}
            </>
          )}
          <Box sx={{ flex: '1 1 auto' }} />
          <Button
            size="small"
            onClick={onAdvancedChange}
            aria-expanded={advanced}
            aria-controls="product-advanced-filters"
          >
            {advanced ? 'Hide' : 'Show'} advanced filters
          </Button>
          <Button size="small" onClick={() => onChange(defaultFilters(filters.reportView))}>
            Reset view
          </Button>
        </Stack>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5 }}>
          {FILTER_DEFINITIONS.filter(({ key }) => PRIMARY_FILTERS.includes(key)).map(field)}
        </Box>
        <Collapse in={advanced} id="product-advanced-filters">
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
              gap: 1.5,
              pt: 0.5,
            }}
          >
            {FILTER_DEFINITIONS.filter(({ key }) => !PRIMARY_FILTERS.includes(key)).map(field)}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Select several values in each field. An empty field includes every value; Unknown selects missing source
            values.
          </Typography>
        </Collapse>
        {filters.reportView === 'low-margin' && (
          <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
            <Typography variant="body2">Grouped margin range</Typography>
            {(['minMargin', 'maxMargin'] as const).map((key) => (
              <TextField
                key={key}
                label={key === 'minMargin' ? 'Minimum margin %' : 'Maximum margin %'}
                type="number"
                size="small"
                value={Number((filters[key] * 100).toFixed(4))}
                onChange={(event) => {
                  const value = Number(event.target.value);
                  if (event.target.value !== '' && Number.isFinite(value)) {
                    onChange({ [key]: value / 100 });
                  }
                }}
                slotProps={{ htmlInput: { step: 0.1 } }}
                sx={{ width: 180, maxWidth: '100%' }}
              />
            ))}
          </Stack>
        )}
        {active.length > 0 && (
          <Stack direction="row" gap={0.75} flexWrap="wrap" aria-label="Active filters">
            {active.map(({ key, label }) => {
              const values = filters.selections[key];
              return (
                <Tooltip key={key} title={values.join(', ') || 'All values'}>
                  <Chip
                    size="small"
                    label={`${label}: ${values.length === 0 ? 'All values' : values.length > 2 ? `${values.length} selected` : values.join(', ')}`}
                    onDelete={() => select(key, saved.selections[key])}
                    sx={{ maxWidth: '100%' }}
                  />
                </Tooltip>
              );
            })}
            <Button size="small" onClick={() => onChange({ selections: saved.selections })}>
              Clear selections
            </Button>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}
